import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react'
import { checkUserExistsByEmail } from '@/lib/firebase'
import type { ArtistOnboarding } from '@/types/models'
import { Button } from '@/components/ui/button'

interface ArtistOnboardingAccountStepProps {
  onboarding: ArtistOnboarding
  onDataChange: (data: Partial<ArtistOnboarding> & { password?: string; confirmPassword?: string }) => void
}

export function ArtistOnboardingAccountStep({ onboarding, onDataChange }: ArtistOnboardingAccountStepProps) {
  const [formData, setFormData] = useState({
    email: onboarding.email || '',
    password: '',
    confirmPassword: ''
  })
  
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [emailCheckingInProgress, setEmailCheckingInProgress] = useState(false)
  const [userAccountExists, setUserAccountExists] = useState<boolean | null>(null)
  const [emailCheckError, setEmailCheckError] = useState<string | null>(null)

  // Update parent component when form data changes
  useEffect(() => {
    const dataToSend = {
      ...formData,
      userAccountExists
    } as any
    onDataChange(dataToSend)
  }, [formData, userAccountExists])

  // Debounced email checking function
  const checkEmailExistence = useCallback(async (email: string) => {
    if (!email || !email.includes('@') || !email.includes('.')) {
      console.log('🚫 Email validation failed - not checking:', email)
      setUserAccountExists(null)
      setEmailCheckError(null)
      return
    }

    console.log('🔄 Starting email check for:', email)
    setEmailCheckingInProgress(true)
    setEmailCheckError(null)

    try {
      const exists = await checkUserExistsByEmail(email)
      console.log('📋 Email check result:', { email, exists })
      setUserAccountExists(exists)
    } catch (error) {
      console.error('❌ Error checking email existence:', error)
      setEmailCheckError('Failed to check email. Please try again.')
      setUserAccountExists(null)
    } finally {
      setEmailCheckingInProgress(false)
    }
  }, [])

  // Debounce email checking
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.email) {
        checkEmailExistence(formData.email)
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [formData.email, checkEmailExistence])

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold">
              1
            </div>
            Create Your Account
          </CardTitle>
          <CardDescription>
            First, let's set up your account. This will allow you to access and manage your profile.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Instagram Username (Read-only) */}
          <div className="space-y-2">
            <Label htmlFor="displayName">Instagram Username</Label>
            <Input
              id="displayName"
              value={onboarding.displayName}
              readOnly
              className="text-lg bg-muted cursor-not-allowed"
            />
            <p className="text-xs text-muted-foreground">
              This is your Instagram username and cannot be changed
            </p>
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <div className="relative">
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="your@email.com"
                className={`pr-10 ${
                  userAccountExists === true ? 'border-orange-500' : 
                  userAccountExists === false ? 'border-green-500' : ''
                }`}
              />
              {emailCheckingInProgress && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                </div>
              )}
              {!emailCheckingInProgress && userAccountExists === true && (
                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-orange-500" />
              )}
              {!emailCheckingInProgress && userAccountExists === false && (
                <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
              )}
            </div>
            
            {/* Existing Account Message */}
            {userAccountExists === true && (
              <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-md">
                <AlertCircle className="h-4 w-4 text-orange-600 flex-shrink-0" />
                <p className="text-sm text-orange-700">
                  This email already has an account. Please enter your existing password to log in.
                </p>
              </div>
            )}
            
            {/* New Account Message */}
            {userAccountExists === false && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-700">
                  Great! We'll create a new account for you.
                </p>
              </div>
            )}
            
            {/* Default message when no check has been performed yet */}
            {userAccountExists === null && formData.email && !emailCheckingInProgress && !emailCheckError && (
              <p className="text-xs text-muted-foreground">
                Enter a valid email address to check account status
              </p>
            )}
            
            {emailCheckError && (
              <p className="text-xs text-destructive">{emailCheckError}</p>
            )}
          </div>

          {/* Password Fields - Only show after email check is complete */}
          {userAccountExists !== null && (
            <div className="space-y-4">
              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password">
                  {userAccountExists === true ? 'Your Password *' : 'Choose Password *'}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder={userAccountExists === true ? 'Enter your existing password' : 'Choose a secure password'}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {userAccountExists === true ? 'Use your existing account password' : 'Minimum 6 characters'}
                </p>
              </div>

              {/* Confirm Password Field - Only for new accounts */}
              {userAccountExists === false && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      placeholder="Confirm your password"
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Must match your password
                  </p>
                </div>
              )}
            </div>
          )}

        </CardContent>
      </Card>
    </div>
  )
}

